import { useState, useMemo } from 'react';
import type { AIWorkflow } from '@erp/shared-types';

export interface WorkflowFilterOption {
  value: AIWorkflow | 'all';
  label: string;
  color?: string;
}

// Centralized workflow labels and colors - used across the entire app
export const WORKFLOW_LABELS: Record<AIWorkflow, string> = {
  EMAIL_SO_INGESTION: 'Email Sales Order',
  PRICE_SHEET_INGESTION: 'Price Sheet',
  AP_INVOICE_INGESTION: 'AP Invoicing',
  AR_COLLECTIONS: 'AR Collections',
  AR_REMITTANCE_INGESTION: 'AR Remittance',
};

export const WORKFLOW_COLORS: Record<AIWorkflow, string> = {
  EMAIL_SO_INGESTION: 'bg-blue-100 text-blue-700',
  PRICE_SHEET_INGESTION: 'bg-green-100 text-green-700',
  AP_INVOICE_INGESTION: 'bg-orange-100 text-orange-700',
  AR_COLLECTIONS: 'bg-purple-100 text-purple-700',
  AR_REMITTANCE_INGESTION: 'bg-teal-100 text-teal-700',
};

// Filter options for workflows
export const WORKFLOW_FILTER_OPTIONS: WorkflowFilterOption[] = [
  { value: 'all', label: 'All Workflows' },
  {
    value: 'EMAIL_SO_INGESTION' as AIWorkflow,
    label: WORKFLOW_LABELS.EMAIL_SO_INGESTION,
    color: WORKFLOW_COLORS.EMAIL_SO_INGESTION,
  },
  {
    value: 'PRICE_SHEET_INGESTION' as AIWorkflow,
    label: WORKFLOW_LABELS.PRICE_SHEET_INGESTION,
    color: WORKFLOW_COLORS.PRICE_SHEET_INGESTION,
  },
  {
    value: 'AP_INVOICE_INGESTION' as AIWorkflow,
    label: WORKFLOW_LABELS.AP_INVOICE_INGESTION,
    color: WORKFLOW_COLORS.AP_INVOICE_INGESTION,
  },
  {
    value: 'AR_COLLECTIONS' as AIWorkflow,
    label: WORKFLOW_LABELS.AR_COLLECTIONS,
    color: WORKFLOW_COLORS.AR_COLLECTIONS,
  },
  {
    value: 'AR_REMITTANCE_INGESTION' as AIWorkflow,
    label: WORKFLOW_LABELS.AR_REMITTANCE_INGESTION,
    color: WORKFLOW_COLORS.AR_REMITTANCE_INGESTION,
  },
];

export function useWorkflowFilter() {
  const [selectedWorkflow, setSelectedWorkflow] = useState<AIWorkflow | 'all'>(
    'all',
  );

  const filteredWorkflow = useMemo(() => {
    return selectedWorkflow === 'all' ? undefined : selectedWorkflow;
  }, [selectedWorkflow]);

  const selectedOption = useMemo(() => {
    return (
      WORKFLOW_FILTER_OPTIONS.find(
        (option) => option.value === selectedWorkflow,
      ) || WORKFLOW_FILTER_OPTIONS[0]
    );
  }, [selectedWorkflow]);

  const resetFilter = () => {
    setSelectedWorkflow('all');
  };

  return {
    selectedWorkflow,
    setSelectedWorkflow,
    filteredWorkflow,
    selectedOption,
    resetFilter,
    options: WORKFLOW_FILTER_OPTIONS,
  };
}
